import styled from '@emotion/styled';
import {
  DateSelectArg,
  EventApi,
  EventClickArg,
  EventContentArg,
  EventInput,
} from '@fullcalendar/core';
import {DragMetaInput} from '@fullcalendar/core/internal';
import interactionPlugin, {Draggable} from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import moment from 'moment/moment';
import React, {useCallback, useContext, useRef, useState} from 'react';
import {showSnackbar, SnackbarType} from 'webapp-lib/Functions';
import {generateRandomUnsafeId, useMountEffect} from 'webapp-lib/utils';

import {showSimpleConfirmationDialog} from '_/sharedComponents/ConfirmationDialog';

import {
  CalendarWorkLogExtendedProps,
  DraggableWorkLogSuggestion,
  EventApiWithWorkLogProps,
  EventTimers,
  WorkLog,
} from '../types';
import {toSeconds} from '../utilities/date';

import {EditWorkLogDialog} from './EditWorkLogDialog';
import {AdditionalEventInfo, Event} from './Event';
import {CALENDAR_RECENT_CONTAINER} from './RecentSearch';
import {CalendarAPIContext} from './TimeTrackerShelf';
import {
  CALENDAR_DRAGGABLE_CARD,
  CALENDAR_DRAGGABLE_CONTENT,
} from './favorites/FavoriteTemplate';
import {CALENDAR_FAVORITE_CONTAINER} from './favorites/Favorites';

const START_WORK_HOUR = '8:00';
const END_WORK_HOUR = '17:00';

const SLOT_DURATION_MINUTES = 15;
const SNAP_TO_RESOLUTION_MINUTES = 5;
const LABEL_INTERVAL_HOURS = 1;
export const DEFAULT_EVENT_DURATION_SECONDS = toSeconds(60);

const DRAG_TO_CREATE_EVENT_PREVIEW: 'highlight' | 'normal' = 'normal';

type CalendarColumnProps = {
  handleEventsChanged: (updatedEvents: EventApiWithWorkLogProps[]) => void;
  initialEvents: EventApiWithWorkLogProps[];
  timer: EventTimers;
  recordingEvent: EventApiWithWorkLogProps | null;
};

const getEventDataFromCard = (cardEl: HTMLElement): DragMetaInput => {
  const contentEl: HTMLElement | null = cardEl.querySelector(
    `#${CALENDAR_DRAGGABLE_CONTENT}`
  );
  if (!contentEl?.dataset.template) {
    return {id: generateRandomUnsafeId()};
  }

  const {color, title, defaultDurationSeconds, activity, description} =
    JSON.parse(contentEl.dataset.template) as DraggableWorkLogSuggestion;
  const extendedProps: CalendarWorkLogExtendedProps = {
    activity,
    description,
  };

  const newEvent: EventInput = {
    id: generateRandomUnsafeId(),
    title,
    backgroundColor: color,
    duration: {
      seconds: defaultDurationSeconds ?? DEFAULT_EVENT_DURATION_SECONDS,
    },
    extendedProps,
  };
  return {...newEvent, create: true};
};

const convertToEventInput = (event: EventApi): EventInput => ({
  id: event.id,
  title: event.title,
  start: event.start!,
  end: event.end!,
  groupId: event.groupId,
  allDay: event.allDay,
  url: event.url,
  classNames: event.classNames,
  display: event.display,
  editable: true,
  overlap: event.overlap,
  constraint: event.constraint,
  backgroundColor: event.backgroundColor,
  borderColor: event.borderColor,
  textColor: event.textColor,
  color: event.backgroundColor,
  extendedProps: event.extendedProps,
});

export const Calendar = ({
  handleEventsChanged,
  initialEvents,
  timer,
  recordingEvent,
}: CalendarColumnProps): React.ReactElement => {
  const calendarRef = useRef<FullCalendar>(null!);
  const draggableCardRef = useRef<{
    favorites: Draggable;
    recent: Draggable;
  } | null>(null);

  const [selectedEvent, setSelectedEvent] =
    useState<EventApiWithWorkLogProps | null>(null);

  const calendarAPI = useCallback(() => calendarRef.current.getApi(), []);
  const calendarContext = useContext(CalendarAPIContext);

  useMountEffect(() => calendarContext.setCalendarAPI(calendarAPI()));

  // init draggable favorites
  useMountEffect(() => {
    if (draggableCardRef.current) {
      return;
    }

    const draggableFavoriteContainer = document.getElementById(
      CALENDAR_FAVORITE_CONTAINER
    );
    const draggableRecentContainer = document.getElementById(
      CALENDAR_RECENT_CONTAINER
    );
    if (!draggableRecentContainer || !draggableFavoriteContainer) {
      return;
    }

    const cardSelector = `div[data-id=${CALENDAR_DRAGGABLE_CARD}]`;
    const options = {
      itemSelector: cardSelector,
      eventData: getEventDataFromCard,
    };
    draggableCardRef.current = {
      favorites: new Draggable(draggableFavoriteContainer, options),
      recent: new Draggable(draggableRecentContainer, options),
    };

    return () => {
      if (draggableCardRef.current) {
        draggableCardRef.current.favorites.destroy();
        draggableCardRef.current.recent.destroy();
        draggableCardRef.current = null;
      }
    };
  });

  const handleEventClassNames = (content: EventContentArg) => {
    if (content.isDragging || content.isMirror) {
      return [];
    }

    const classes: string[] = [];
    const {event} = content;

    if (!event.extendedProps.activity) {
      classes.push('work-log-unassigned');
    }
    if (recordingEvent && recordingEvent.id === event.id) {
      classes.push('work-log-is-tracking');
    }

    return classes;
  };

  const handleRenderEventContent = (eventInfo: EventContentArg) => (
    <Event
      eventInfo={eventInfo}
      recordingEvent={recordingEvent}
      timer={timer}
    />
  );

  const handleDateRangeSelected = (selectInfo: DateSelectArg) => {
    const event = calendarAPI().addEvent({
      id: generateRandomUnsafeId(),
      start: selectInfo.start,
      end: selectInfo.end,
      editable: true,
    });

    setSelectedEvent(event);
    calendarAPI().unselect(); // clear date selection
  };

  const handleEventClicked = (eventInfo: EventClickArg) => {
    setSelectedEvent(eventInfo.event);
  };

  const handleEventEdited = (edited: WorkLog) => {
    const event = calendarAPI().getEventById(edited.id);
    if (!event) {
      showSnackbar({
        text: 'Änderungen konnten nicht gespeichert werden.',
        type: SnackbarType.WARNING,
      });
      return;
    }

    event.setDates(edited.start, edited.end);
    event.setProp('title', edited.title);
    event.setProp('color', edited.color);
    event.setExtendedProp('activity', edited.activity);
    event.setExtendedProp('description', edited.description);
  };

  const handleEventDeleted = (event: EventApiWithWorkLogProps) => {
    showSimpleConfirmationDialog({
      headerText: 'Aktivität Löschen',
      message: 'Wollen Sie die Aktivität wirklich löschen?',
      buttonProps: {danger: true, confirmationButtonText: 'Löschen'},
      onConfirmed: () => {
        if (recordingEvent?.id === event.id) {
          timer.stop();
        }
        event.remove();
        setSelectedEvent(null);
      },
    });
  };

  function handleEditDialogClosed() {
    if (selectedEvent) {
      // delete selected event range if nothing was entered in the dialog
      const event = calendarAPI().getEventById(selectedEvent.id);
      if (event?.title === '') {
        event.remove();
      }
    }
    setSelectedEvent(null);
  }

  const dialog =
    selectedEvent !== null ? (
      <EditWorkLogDialog
        data={selectedEvent}
        isOpen={true}
        onClose={handleEditDialogClosed}
        onDelete={handleEventDeleted}
        onEdit={handleEventEdited}
      />
    ) : null;

  return (
    <>
      {dialog}
      <CalendarTheme>
        <FullCalendar
          ref={calendarRef}
          allDaySlot={false}
          businessHours={{
            daysOfWeek: [1, 2, 3, 4, 5],
            startTime: START_WORK_HOUR,
            endTime: END_WORK_HOUR,
          }}
          buttonText={{today: 'Heute'}}
          dayHeaderFormat={{
            year: 'numeric',
            month: '2-digit',
            day: 'numeric',
            weekday: 'long',
          }}
          droppable={true}
          editable={true}
          eventClassNames={handleEventClassNames}
          eventClick={handleEventClicked}
          eventContent={handleRenderEventContent}
          eventsSet={handleEventsChanged} // called after events are initialized/added/changed/removed
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            omitZeroMinute: false,
            hour12: false,
          }}
          expandRows={true}
          firstDay={1}
          headerToolbar={{
            left: 'prev',
            center: 'today',
            right: 'next',
          }}
          initialEvents={initialEvents.map(convertToEventInput)}
          initialView='timeGridDay'
          locale='de-CH'
          nowIndicator={true}
          plugins={[timeGridPlugin, interactionPlugin]}
          scrollTime={moment().subtract(2, 'hours').format('HH:mm')}
          scrollTimeReset={false}
          select={handleDateRangeSelected}
          selectable={true}
          selectMirror={DRAG_TO_CREATE_EVENT_PREVIEW === 'normal'}
          slotDuration={{minutes: SLOT_DURATION_MINUTES}}
          slotLabelFormat={{
            hour: 'numeric',
            minute: '2-digit',
            omitZeroMinute: false,
            hour12: false,
          }}
          slotLabelInterval={{hours: LABEL_INTERVAL_HOURS}}
          snapDuration={{minutes: SNAP_TO_RESOLUTION_MINUTES}}
          weekends={false}
        />
      </CalendarTheme>
    </>
  );
};

const CalendarTheme = styled.div`
  --fc-button-bg-color: var(--color-core-primary);
  --fc-button-hover-bg-color: var(--color-core-primary);
  --fc-button-border-color: var(--color-box-border);
  --fc-button-hover-border-color: var(--color-state-hover);
  --fc-today-bg-color: transparent;

  height: 100%;

  .work-log-unassigned {
    border-right: var(--color-alerts-info) 10px solid !important;
  }

  .work-log-is-tracking {
    border-right: var(--color-state-active) 10px solid !important;
  }

  & .fc-media-screen {
    height: 100%;
  }

  & .fc-timegrid-slot {
    height: 1em;
  }

  & .fc-event {
    cursor: auto;
  }

  .fc-timegrid-event-short {
    & ${AdditionalEventInfo} {
      display: none !important;
    }
  }

  .fc-timegrid-now-indicator-container {
    .fc-timegrid-now-indicator-line {
      border-width: 3px 0 0;
    }

    .fc-timegrid-now-indicator-arrow {
      scale: 2;
    }
  }

  & .fc-scrollgrid {
    border: 1px solid var(--color-core-primary);
  }

  & .fc-timegrid-slot {
    border-color: var(--color-box-border);
  }

  & .fc-toolbar.fc-header-toolbar {
    margin-bottom: var(--gutter);
  }

  .fc-event-resizer:after {
    content: '';
    display: block;
    border-top: 4px double;
    border-color: var(--fc-event-text-color);
    margin: 0 auto;
    width: 12px;
  }
`;
